import '../shared/base.css';
import '../shared/ui.css';
import './match.css';
import {
  createScenario, createUnit, tick, hashState, hashToHex, placementSpot, placementDistance, unusedKinds, playerUnits, phaseTicksLeft, ammoLeft,
  INVADERS, WEAPONS, BAR_ORDER, MAP_W, MAP_H, FP, UNIT_W, UNIT_H, UNIT_HP, TICKS_PER_SECOND, PLACEMENT_ENEMY_DISTANCE,
  PHASE_PLACEMENT, PHASE_AIM, PHASE_RETREAT, PHASE_RESOLVE, PHASE_OVER, DRAW, WEAPON_BAZOOKA, WEAPON_DOLL, lavaRiseAt,
  REMOTE_SIZE, REMOTE_STEP_DEG, remoteRefusal, ERUPTION_EVERY, WEAPON_JETPACK, JETPACK_FUEL, playable,
  WEAPON_DRILL, DRILL_TICKS, WEAPON_PISTOL, PISTOL_SHOTS, PHASE_GHOST, WEAPON_MINE, MINE_HALF, CHEST_W, MAX_CHESTS, inRotation, isOut,
  type Command, type CommandInput, type GameState, type MatchState, type ReplayLog, type ScenarioDesc, type Unit,
} from '@invaders/game';
import { STATE_LIQUID } from '@invaders/sim';
import { Camera } from '../shared/camera';
import { ViewRenderer } from '../shared/view-renderer';
import { Backdrop } from '../shared/backdrop';
import { themeFor } from '../shared/themes';
import { Minimap } from '../shared/minimap';
import { LocalTransport } from '../shared/transport';
import { startFixedLoop } from '../shared/fixed-loop';
import { viewScale } from '../shared/view-scale';
import { drawBatteries, drawMachines, drawChests, drawImmersion, drawMines, drawProjectiles, drawSteles, drawSurges, drawUnit, toScreen } from '../shared/draw-units';
import { UnitAnimator, drawFrame, FRAME, spritesReady } from '../shared/sprites';
import { bootDone } from '../shared/boot';
import { weaponIcon } from '../shared/weapon-icons';
import { drawThrowArc, throwArc } from '../shared/aim';
import { uiIcon, uiIconHtml } from '../shared/ui-icons';
import { sound } from '../shared/sound';
import { music, moodOf } from '../shared/music';
import { loadOptions } from '../shared/options';
import { buildOptionsPanel } from '../shared/options-panel';
import { Effects } from '../shared/effects';
import { downloadJson } from '../shared/download';
import { DEFAULT_TEAM_NAMES, MAX_TEAM_NAME, TEAM_COLORS } from '../shared/teams';
import { Director } from './director';
import { PlacementZones } from './placement-zones';
import { KICK_CONFIRM_MS, linkPlacementOptions, matchOptions, setupLobby, setupMatchSize } from './lobby';
import { discordInstance, startDiscord } from '../shared/discord';
import { track } from '../shared/analytics';
import { fillMapSelect, resolveMap } from '../shared/maps-ui';
import { isDefaultArsenal, setupArsenalPicker } from '../shared/arsenal-ui';
import { Gestures, padState } from '../shared/gestures';
import { Lockstep } from '../net/lockstep';
import { HASH_EVERY_TICKS, type MatchResult, type RoomMessage, type StartMessage } from '../net/protocol';
import type { NetClient } from '../net/net-client';

const EDGE_SCROLL_PX = 32;
const EDGE_SCROLL_CELLS_PER_S = 240;
/** Time to charge a throw to full power. Not in the GDD: tuning value. */
const CHARGE_MS = 1500;
/** Distance of the crosshair from the unit, in cells. */
const CROSSHAIR = 36;
/** Pixels a right-button press may move and still count as a click (cancel) rather than a drag. */
const DRAG_THRESHOLD = 5;
/** Time to confirm « Fin de tour » with a second click (or F) while the unit has not fired. */
const END_CONFIRM_MS = 3000;

declare global {
  interface Window {
    /** For scripted browser tests. */
    __match?: {
      state: () => GameState | null; send: (c: CommandInput) => void; camera: Camera; names: () => string[];
      /** Online: the local seat, and the fingerprints sent to the server (tick → hash). */
      seat: () => number; hashes: () => Record<number, string>; desync: () => string | null;
    };
  }
}

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>('view');
const ctx = canvas.getContext('2d')!;
const dpr = () => window.devicePixelRatio || 1;

// --- setup screen ---------------------------------------------------------------------------------
let teamNames: string[] = [];
const namesBox = $('names');
function buildNameInputs(n: number): void {
  const old = [...namesBox.querySelectorAll('input')].map((i) => i.value);
  namesBox.innerHTML = '';
  for (let p = 0; p < n; p++) {
    const label = document.createElement('label');
    label.innerHTML = `<span style="color:${TEAM_COLORS[p]}">Team ${p + 1}</span>`;
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = MAX_TEAM_NAME;
    input.value = old[p] ?? (p === 0 && loadOptions().teamName ? loadOptions().teamName : DEFAULT_TEAM_NAMES[p]);
    label.appendChild(input);
    namesBox.appendChild(label);
  }
}
// The same controls as online: players and invaders per player as buttons, the rounds before the end of the world on a slider.
const size = setupMatchSize('', buildNameInputs);
$<HTMLInputElement>('seed').value = String((Math.random() * 1e9) >>> 0);
const refreshPreview = fillMapSelect($<HTMLSelectElement>('map'), $('map-blurb'), $<HTMLCanvasElement>('map-preview'), () => Number($<HTMLInputElement>('seed').value) >>> 0);
$<HTMLInputElement>('seed').addEventListener('input', refreshPreview);
const arsenal = setupArsenalPicker($('arsenal-btn'), $('arsenal-sum'));
linkPlacementOptions($<HTMLInputElement>('free'), $<HTMLInputElement>('hidden'));

music.play('menu');

const modeTabs = [...$('mode-tabs').querySelectorAll('button')];
function showMode(mode: 'online' | 'local'): void {
  modeTabs.forEach((b) => b.classList.toggle('on', b.dataset.tab === mode));
  $('tab-online').classList.toggle('hidden', mode !== 'online');
  $('tab-local').classList.toggle('hidden', mode !== 'local');
}
modeTabs.forEach((b) => { b.onclick = () => showMode(b.dataset.tab as 'online' | 'local'); });
// From the title screen: « Sur ce PC » opens on the local tab.
if (new URLSearchParams(location.search).get('mode') === 'local') showMode('local');
// Discord Activities (author's addition): online only, no title screen to go back to; the handshake Discord waits for.
if (discordInstance) {
  $('mode-tabs').classList.add('hidden');
  document.querySelector('.setup-head .menulink')?.classList.add('hidden');
  void startDiscord();
}

// --- game state -----------------------------------------------------------------------------------
let scenario: ScenarioDesc | null = null;
let state: GameState | null = null;
let log: Command[] = [];
let pending: Command[] = [];
const transport = new LocalTransport();
transport.onCommand((cmd) => { pending.push(cmd); });

/**
 * Online match: the commands go to the relay server, which stamps their tick; the match only advances on
 * the ticks it confirmed (lockstep), so every browser computes the same match.
 */
interface Online {
  net: NetClient;
  lock: Lockstep;
  /** The local player's seat (= player index in the match). */
  seat: number;
  room: RoomMessage;
  /** Fingerprints sent to the server, by tick (debug and tests). */
  hashes: Record<number, string>;
  desync: string | null;
  lost: boolean;
  /** The end of the match was sent to the server (for its admin page). */
  resultSent: boolean;
}
let online: Online | null = null;

/**
 * Per unit id, for the end of an online match (MatchResult): the damage it took (at most the HP it had left: `hp`, its
 * HP at the end of the previous tick), the round it died in.
 */
let tally: { damage: number; died: number; offMap: boolean; hp: number }[] = [];

const send = (input: CommandInput) => {
  if (!state) return;
  if (online) { online.net.send({ t: 'cmd', cmd: input }); return; }
  transport.send({ ...input, tick: state.tick } as Command);
};

/** True if this browser plays for `player`: always on this PC, only its own seat online. */
const controls = (player: number) => !online || online.seat === player;

const cam = new Camera(MAP_W, MAP_H);
const renderer = new ViewRenderer();
const minimap = new Minimap(MAP_W, MAP_H);

/** The map's look (GDD previews): the sky behind, the terrain's colours. Rebuilt when the map changes. */
let lookId = '';
let backdrop: Backdrop | null = null;
function applyLook(mapId: string): void {
  if (mapId === lookId) return;
  lookId = mapId;
  const theme = themeFor(mapId);
  renderer.setTheme(theme);
  minimap.setTheme(theme);
  backdrop = new Backdrop(theme, MAP_W, MAP_H);
}
document.body.appendChild(minimap.canvas);
const effects = new Effects();
const animator = new UnitAnimator();
const zones = new PlacementZones();
sound.follow(cam);
const director = new Director();
let wide = false;

function fit(): void {
  canvas.width = Math.round(window.innerWidth * dpr());
  canvas.height = Math.round(window.innerHeight * dpr());
  const s = viewScale(canvas.width, canvas.height);
  cam.resize(canvas.width, canvas.height, wide ? s / 2 : s);
}
window.addEventListener('resize', fit);
fit();
cam.centerOn(MAP_W / 2, MAP_H / 2);

function start(): void {
  const players = size.players();
  const unitsPerPlayer = size.units();
  teamNames = [...namesBox.querySelectorAll('input')].map((i, p) => i.value.trim().slice(0, MAX_TEAM_NAME) || DEFAULT_TEAM_NAMES[p]);
  scenario = {
    terrain: resolveMap($<HTMLSelectElement>('map').value), seed: Number($<HTMLInputElement>('seed').value) >>> 0, width: MAP_W, height: MAP_H,
    mode: 'match',
    match: {
      players, unitsPerPlayer, roundsBeforeLava: size.lava(),
      // The host's choice of weapons (left out: the GDD's arsenal), and its options (left out: the GDD's rules).
      ...(isDefaultArsenal(arsenal()) ? {} : arsenal()),
      ...matchOptions({
        turn: Number($<HTMLSelectElement>('turn-time').value), friendlyFire: $<HTMLInputElement>('ff').checked, hidden: $<HTMLInputElement>('hidden').checked,
        free: $<HTMLInputElement>('free').checked, ghosts: $<HTMLInputElement>('ghosts').checked,
      }),
    },
  };
  online = null;
  beginMatch(scenario);
  track('start', { mode: 'local' });
}

/** Common to both modes: a fresh match on screen. */
function beginMatch(desc: ScenarioDesc): void {
  scenario = desc;
  state = createScenario(desc);
  log = [];
  pending = [];
  tally = [];
  weapon = WEAPON_BAZOOKA;
  placingKind = -1;
  lastTurn = 0;
  resetDesignations();
  $('setup').classList.add('hidden');
  $('end').classList.add('hidden');
  wide = true; // placement opens on the wide view
  fit();
  cam.centerOn(MAP_W / 2, MAP_H / 2);
  music.play(moodOf(state.map.id));
}

function startOnline(msg: StartMessage, net: NetClient, room: RoomMessage): void {
  // Also on a return (reload, lost connection): the match is computed again from its start.
  if (online && online.net !== net) online.net.close();
  teamNames = msg.names;
  beginMatch(msg.scenario);
  const lock = new Lockstep();
  lock.receive(msg.upTo, msg.cmds);
  online = { net, lock, seat: msg.you, room, hashes: {}, desync: null, lost: false, resultSent: false };
  if (msg.you >= 0) track('start', { mode: 'online' });
  const session = online;
  net.on('frame', (f) => { if (online === session) session.lock.receive(f.upTo, f.cmds); });
  net.on('desync', (d) => { if (online === session) session.desync = `Desync detected at tick ${d.tick}: please report it!`; });
  net.on('ping', (p) => {
    if (online !== session) return;
    pings.push({ x: p.x, y: p.y, seat: p.seat, at: performance.now() });
    sound.ping(p.x, p.y);
  });
  net.onClose(() => { if (online === session) { session.lost = true; reconnect(session); } });
}

/** Lost connection during a match: try again every 2 s with the token of the seat. */
function reconnect(session: Online): void {
  window.setTimeout(async () => {
    if (online !== session) return;
    try {
      await session.net.connect();
      session.lost = false;
      // A spectator watches again; a player takes its seat back with its token.
      session.net.send(session.seat < 0 ? { t: 'watch', code: session.room.code } : { t: 'join', code: session.room.code, token: session.room.token ?? undefined, name: teamNames[session.seat] });
    } catch { reconnect(session); }
  }, 2000);
}

const lobby = setupLobby({
  onStart: startOnline,
  onRoom: (room) => {
    if (!online) return;
    online.room = room;
    if (!$('pause').classList.contains('hidden')) renderKickList();
  },
  // Excluded by the host: the match is left (no reconnection), the lobby shows why.
  onKicked: () => {
    if (!online) return;
    online = null;
    openPause(false);
    quitMatch();
  },
});

$('start').onclick = start;
$('again').onclick = () => quitMatch();

// --- local interface state (not part of the game) ------------------------------------------------
let weapon = WEAPON_BAZOOKA;
let placingKind = -1;
let lastTurn = 0;
/** Seconds left on the timer at the last tick, for the ticking of the last 5 s. */
let lastSeconds = 0;
let mouse = { x: 0, y: 0, inside: false };
let charging: { start: number } | null = null;
/** Voodoo doll: the enemy designated before the throw (-1 = none yet). */
let linkTarget = -1;
/** Remote: the square picked (its centre), and the rotation chosen with the wheel (steps of 15°). */
let remotePick: { sx: number; sy: number } | null = null;
let remoteRot = 0;
/** Flamethrower: the button is held, the jet follows the aim (sent when the angle changes). */
let holding = false;
/**
 * Drill: started at `since` (its command takes a moment to come back online), the button still `held` (the drill
 * follows the aim); `before`: the weapon in hand before it, taken back once the drilling is over.
 */
let drilling: { since: number; held: boolean } | null = null;
let beforeDrill = WEAPON_BAZOOKA;
let lastAim = NaN;
let rightPress: { x: number; y: number; dragging: boolean } | null = null;
const keys = { left: false, right: false };
/**
 * Touch screens (phones, tablets): the touch interface is on (body.touch) from the first touch, off at the first
 * mouse click. `aimCell`: where the finger put the cursor, in cells (the cursor stays on that point when the view
 * moves); `lastTouchAt`: when a finger last touched (the mouse events that follow a touch are ignored).
 */
let touchUi = false;
let aimCell: [number, number] | null = null;
let lastTouchAt = -Infinity;

const m = () => state?.match ?? null;
const selectedUnit = (): Unit | null => { const mm = m(); return mm && mm.selected >= 0 && state ? state.units[mm.selected] : null; };
const myTurn = () => { const mm = m(); return !!mm && (mm.phase === PHASE_AIM || mm.phase === PHASE_RETREAT); };
/** The current player is played from this browser. */
const mine = () => { const mm = m(); return !!mm && controls(mm.currentPlayer); };
const canAct = () => { const mm = m(); return !!mm && mm.locked && myTurn() && mine(); };
const mouseCell = () => cam.screenToCell(mouse.x * dpr(), mouse.y * dpr());

function unitCenterCells(u: Unit): [number, number] { return [u.x / FP + UNIT_W / 2, u.y / FP + UNIT_H / 2]; }

/** Integer aim angle from the unit to the cursor (0 = right, 90 = up). */
function aimAngle(u: Unit): number {
  const [ux, uy] = unitCenterCells(u);
  const [cx, cy] = [cam.x + (mouse.x * dpr()) / cam.scale, cam.y + (mouse.y * dpr()) / cam.scale];
  return Math.round((Math.atan2(-(cy - uy), cx - ux) * 180) / Math.PI);
}

function chargePower(now: number): number {
  return charging ? Math.min(100, Math.round(((now - charging.start) / CHARGE_MS) * 100)) : 0;
}

function fire(power: number): void {
  const mm = m();
  const u = selectedUnit();
  if (!mm || !u || !canAct() || mm.fired) return;
  send({ type: 'fire', player: mm.currentPlayer, unit: u.id, weapon, angle: aimAngle(u), power, ...(weapon === WEAPON_DOLL ? { link: linkTarget } : {}) });
}

function resetDesignations(): void {
  linkTarget = -1;
  remotePick = null;
  remoteRot = 0;
}

function unitUnderCursor(): Unit | null {
  if (!state) return null;
  const [x, y] = mouseCell();
  return state.units.find((u) => u.alive && x >= u.x / FP && x < u.x / FP + UNIT_W && y >= u.y / FP && y < u.y / FP + UNIT_H) ?? null;
}

/** Next (dir 1) or previous (dir -1) living unit of the current player. */
function cycle(dir: 1 | -1): void {
  const mm = m();
  if (!state || !mm || mm.phase !== PHASE_AIM || mm.locked || !mine()) return;
  const units = playerUnits(state, mm.currentPlayer).filter((u) => u.alive);
  if (units.length < 2) return;
  const i = units.findIndex((u) => u.id === mm.selected);
  const next = units[(i + dir + units.length) % units.length];
  send({ type: 'select', player: mm.currentPlayer, unit: next.id });
  sound.select();
  director.glideTo(cam, ...unitCenterCells(next));
}

/** A unit of the turn chosen (a card, a click on it); the one already highlighted: ACTION. */
function pickUnit(id: number): void {
  const mm = m();
  const u = state?.units[id];
  if (!mm || !u || mm.phase !== PHASE_AIM || mm.locked || !mine() || u.player !== mm.currentPlayer || !playable(u)) return;
  if (id === mm.selected) { lockUnit(); return; }
  send({ type: 'select', player: mm.currentPlayer, unit: id });
  sound.select();
  director.glideTo(cam, ...unitCenterCells(u));
}

/** ACTION: the highlighted unit is the one of the turn (then move, weapons, one shot). */
function lockUnit(): void {
  const mm = m();
  if (!mm || !mine() || mm.phase !== PHASE_AIM || mm.locked || mm.selected < 0) return;
  send({ type: 'lock', player: mm.currentPlayer });
  sound.lock();
}

// --- input ----------------------------------------------------------------------------------------
/** A left click, or a tap: a ping (online, for who is not playing), the ghost turn's action, a placement, a unit picked, or the weapon. */
function primaryPress(): void {
  const mm = m();
  if (!state || !mm) return;
  if (online && !mine()) { sendPing(); return; }
  if (!mine()) return;
  if (mm.phase === PHASE_GHOST) {
    // Ghost turn: a chest over the column, or a mine on the ground under the point.
    const [x, y] = mouseCell();
    send({ type: 'ghost', player: mm.currentPlayer, action: ghostAction, x, y });
    sound.place();
    return;
  }
  if (mm.phase === PHASE_PLACEMENT) {
    if (placingKind < 0) return;
    const [x, y] = mouseCell();
    if (!placementSpot(state, mm.currentPlayer, x, y, placeDistance(state, mm.currentPlayer)).ok) return;
    send({ type: 'place', player: mm.currentPlayer, kind: placingKind, x, y });
    sound.place();
    placingKind = -1;
    return;
  }
  if (mm.phase === PHASE_AIM && !mm.locked) {
    const u = unitUnderCursor();
    if (u && u.player === mm.currentPlayer && u.id !== mm.selected) pickUnit(u.id);
    return;
  }
  if (canAct() && !mm.fired && mm.phase === PHASE_AIM) weaponDown();
}

/** The weapon's button pressed (the left button, or FIRE on a touch screen), at the cursor. */
function weaponDown(): void {
  const mm = m();
  if (!state || !mm) return;
  const mode = WEAPONS[weapon].mode;
  if (mode === 'direct') fire(0);
  else if (mode === 'target') {
    const [tx, ty] = mouseCell();
    const u = selectedUnit();
    if (u) send({ type: 'fire', player: mm.currentPlayer, unit: u.id, weapon, angle: 0, power: 0, tx, ty });
  }
  else if (mode === 'hold') { fire(0); holding = true; lastAim = NaN; }
  else if (mode === 'drill') {
    // A move, not the shot: the unit bores its way while the button is held, steered by the mouse.
    const u = selectedUnit();
    if (u && u.drill === 0 && !drilling) {
      send({ type: 'drill', player: mm.currentPlayer, unit: u.id, angle: aimAngle(u) });
      drilling = { since: performance.now(), held: true };
      lastAim = NaN;
    }
  }
  else if (mode === 'link' && linkTarget < 0) {
    // First designate the enemy to bind to the doll.
    const t = unitUnderCursor();
    if (t && t.player !== mm.currentPlayer) linkTarget = t.id;
  } else if (mode === 'remote') {
    const [x, y] = mouseCell();
    if (!remotePick) remotePick = { sx: x, sy: y };
    else {
      const u = selectedUnit();
      const move = { sx: remotePick.sx, sy: remotePick.sy, tx: x, ty: y, rot: remoteRot };
      const why = remoteRefusal(state, move);
      if (u && why === null) {
        send({ type: 'fire', player: mm.currentPlayer, unit: u.id, weapon, angle: 0, power: 0, ...move });
        resetDesignations();
      } else if (why) refuse(`Remote: ${why}, pick another spot`);
    }
  } else charging = { start: performance.now() };
}

/** The weapon's button released: the charged throw goes, the flamethrower and the drill stop. */
function weaponUp(): void {
  if (holding) {
    holding = false;
    const mm = m();
    const u = selectedUnit();
    if (mm && u && state?.jets.some((j) => j.unit === u.id)) send({ type: 'release', player: mm.currentPlayer, unit: u.id });
  }
  if (charging) {
    fire(chargePower(performance.now()));
    charging = null;
  }
  if (drilling?.held) {
    drilling.held = false;
    const mm = m();
    const u = selectedUnit();
    if (mm && u) send({ type: 'release', player: mm.currentPlayer, unit: u.id });
  }
}

/** Compatibility mouse events that a touch may still send: ignored (the touch has its own handling). */
const fromTouch = () => performance.now() - lastTouchAt < 800;

canvas.addEventListener('contextmenu', (e) => e.preventDefault());
canvas.addEventListener('mousedown', (e) => {
  if (!state || !m() || fromTouch()) return;
  if (e.button === 2) { rightPress = { x: e.clientX, y: e.clientY, dragging: false }; return; }
  // Online, a ping: the middle button for everyone, the left one for who is not playing now (others, dead, spectators).
  if (online && e.button === 1) { e.preventDefault(); sendPing(); return; }
  if (e.button === 0) primaryPress();
});
window.addEventListener('mouseup', (e) => {
  if (fromTouch()) return;
  if (e.button === 0) weaponUp();
  if (e.button === 2 && rightPress) {
    // GDD: right click = cancel, back to the previous step.
    if (!rightPress.dragging) cancelStep();
    rightPress = null;
  }
});
window.addEventListener('mousemove', (e) => {
  if (fromTouch()) return;
  if (rightPress) {
    const dx = e.clientX - mouse.x;
    const dy = e.clientY - mouse.y;
    if (!rightPress.dragging && Math.hypot(e.clientX - rightPress.x, e.clientY - rightPress.y) > DRAG_THRESHOLD) rightPress.dragging = true;
    if (rightPress.dragging && mouse.inside) {
      cam.panByScreen(dx * dpr(), dy * dpr());
      director.takeOver();
    }
  }
  mouse = { x: e.clientX, y: e.clientY, inside: true };
  aimCell = null;
});
document.addEventListener('mouseleave', () => { mouse.inside = false; });
canvas.addEventListener('wheel', (e) => {
  e.preventDefault();
  // GDD: with the remote, the wheel turns the square by steps of 15°.
  if (remotePick && WEAPONS[weapon].mode === 'remote') { turnRemote(e.deltaY > 0 ? 1 : -1); return; }
  setWide(e.deltaY > 0, e.clientX, e.clientY);
}, { passive: false });
minimap.canvas.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  const [x, y] = minimap.cellAt(e);
  cam.centerOn(x, y);
  director.takeOver();
});

/** The wide view (half the scale) on or off, keeping the cell under the screen point (sx, sy) in place (CSS pixels). */
function setWide(next: boolean, sx: number, sy: number): void {
  if (next === wide) return;
  wide = next;
  const s = viewScale(canvas.width, canvas.height);
  cam.zoomAt(wide ? s / 2 : s, sx * dpr(), sy * dpr());
}

/** Remote: the square turns by steps of 15°. */
function turnRemote(step: number): void {
  remoteRot = (remoteRot + step + 24) % 24;
}

function sendWalk(): void {
  const mm = m();
  const u = selectedUnit();
  if (!mm || !u || !canAct()) return;
  const dir = keys.left === keys.right ? 0 : keys.left ? -1 : 1;
  send({ type: 'walk', player: mm.currentPlayer, unit: u.id, dir });
}
/** Keys: ← → or A D (by position: Q D on AZERTY) walk; Espace, ↑ or W (Z on AZERTY) jump, or thrust the jetpack. */
const isLeft = (e: KeyboardEvent) => e.key === 'ArrowLeft' || e.code === 'KeyA';
const isRight = (e: KeyboardEvent) => e.key === 'ArrowRight' || e.code === 'KeyD';
const isJump = (e: KeyboardEvent) => e.key === ' ' || e.key === 'ArrowUp' || e.code === 'KeyW';
/** The thrust command sent with the jump key held (so that its release turns it off). */
let thrusting = false;

window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { openPause($('pause').classList.contains('hidden')); return; }
  if (e.target instanceof HTMLInputElement || !$('pause').classList.contains('hidden')) return;
  const mm = m();
  const playing = !!mm && $('end').classList.contains('hidden');
  // Shortcuts: Entrée = ACTION, Tab / Maj+Tab = next / previous unit, F = end of turn, 1 to 9 = weapons of the bar.
  const digit = /^(?:Digit|Numpad)([1-9])$/.exec(e.code);
  // Ghost turn: C = a chest, M = a mine (by position: M is the key right of L on QWERTY, of ù's left on AZERTY).
  if (playing && mm.phase === PHASE_GHOST && mine() && (e.code === 'KeyC' || e.code === 'Semicolon' || e.code === 'KeyM')) {
    ghostAction = e.code === 'KeyC' ? 'chest' : 'mine';
    sound.click();
    return;
  }
  if (playing && e.key === 'Enter') { e.preventDefault(); lockUnit(); return; }
  if (playing && e.key === 'Tab') { e.preventDefault(); cycle(e.shiftKey ? -1 : 1); return; }
  if (playing && e.code === 'KeyF' && !e.repeat) { endTurnPressed(); return; }
  if (playing && digit) {
    const b = $('weaponband').style.display !== 'none' ? $('weapons').querySelectorAll<HTMLButtonElement>('button.slot')[Number(digit[1]) - 1] : undefined;
    if (b && !b.disabled) b.click();
    return;
  }
  if (isLeft(e) || isRight(e)) {
    e.preventDefault();
    if (e.repeat) return;
    if (isLeft(e)) keys.left = true; else keys.right = true;
    sendWalk();
  } else if (isJump(e)) {
    e.preventDefault();
    if (!e.repeat) jumpDown();
  } else if (e.key.toLowerCase() === 'l' && state && scenario) {
    // Debug: export the command log, to replay it on /replay.html.
    const out: ReplayLog = { version: 1, scenario, ticks: state.tick, commands: online ? online.lock.log.filter((c) => c.tick < state!.tick) : log, expectedHash: hashToHex(hashState(state)) };
    downloadJson(`invaders-match-${scenario.seed}-${state.tick}.json`, out);
  }
});
/** The jump key (or JUMP) pressed: a jump, or the jetpack's thrust while it is held. */
function jumpDown(): void {
  const mm = m();
  const u = selectedUnit();
  if (!mm || !u || !canAct()) return;
  if (u.jet > 0) { thrusting = true; send({ type: 'thrust', player: mm.currentPlayer, unit: u.id, on: true }); }
  else send({ type: 'jump', player: mm.currentPlayer, unit: u.id });
}
function jumpUp(): void {
  if (!thrusting) return;
  thrusting = false;
  const mm = m();
  const u = selectedUnit();
  if (mm && u && canAct()) send({ type: 'thrust', player: mm.currentPlayer, unit: u.id, on: false });
}

window.addEventListener('keyup', (e) => {
  if (isJump(e) && thrusting) { jumpUp(); return; }
  if (isLeft(e)) keys.left = false;
  else if (isRight(e)) keys.right = false;
  else return;
  sendWalk();
});

$('prev').onclick = () => cycle(-1);
$('next').onclick = () => cycle(1);
$('action').onclick = lockUnit;
// ACTION is a fist (author's choice), on its button, on the card of the highlighted unit and in the hints.
$('action').innerHTML = uiIconHtml('fist');
document.querySelectorAll('#roster .fist').forEach((el) => { el.innerHTML = uiIconHtml('fist'); });
document.documentElement.style.setProperty('--fist', `url(${uiIcon('fist')})`);
// The HUD's buttons never take the focus: Espace and Entrée stay the game's keys.
document.addEventListener('mousedown', (e) => { if (e.target instanceof Element && e.target.closest('.hud button')) e.preventDefault(); });

// --- cancel (GDD: right click = cancel, back to the previous step; also the Annuler button) -------------------
/** What a cancel would undo now: the charge, a designation (remote, doll), the invader to place, or ACTION. */
function cancellable(): 'charge' | 'designation' | 'placing' | 'action' | null {
  const mm = m();
  if (!mm || !mine()) return null;
  if (charging) return 'charge';
  if (remotePick || linkTarget >= 0) return 'designation';
  if (mm.phase === PHASE_PLACEMENT && placingKind >= 0) return 'placing';
  if (mm.phase === PHASE_AIM && mm.locked && !mm.moved && !mm.fired && mm.pistolShots === 0) return 'action';
  return null;
}

function cancelStep(): void {
  const what = cancellable();
  const mm = m();
  if (what === 'charge') charging = null;
  else if (what === 'designation') resetDesignations();
  else if (what === 'placing') placingKind = -1;
  else if (what === 'action' && mm) send({ type: 'unlock', player: mm.currentPlayer });
  if (what) sound.click();
}
$('cancel').onclick = cancelStep;

/**
 * « Fin de tour » (or F): ends the turn now (the resolution, then the next player). Before the shot, a second press
 * within 3 s confirms it (a turn given away by mistake costs a lot); during the retreat, at once.
 */
let endArmedUntil = 0;
function endTurnPressed(): void {
  const mm = m();
  if (!mm || !mine() || (mm.phase !== PHASE_AIM && mm.phase !== PHASE_RETREAT && mm.phase !== PHASE_GHOST)) return;
  const now = performance.now();
  if (mm.phase === PHASE_AIM && mm.pistolShots === 0 && now > endArmedUntil) { endArmedUntil = now + END_CONFIRM_MS; sound.click(); return; }
  endArmedUntil = 0;
  send({ type: 'endTurn', player: mm.currentPlayer });
  sound.endTurn();
}
$('endturn').onclick = endTurnPressed;
// Small screens: the two buttons are icons (their words come back in the confirmation, and in the tooltips).
$('cancel').querySelector('img')!.src = uiIcon('cancel');
$('endturn').querySelector('img')!.src = uiIcon('endTurn');

/**
 * The weapon band (small screens): the bar scrolls sideways (a finger, the wheel, or ◀ ▶ at its ends, shown only when
 * the weapons do not fit); the weapon in hand is kept in view.
 */
let bandWeapon = -1;
function renderBand(bar: HTMLElement): void {
  const band = $('weaponband');
  const over = bar.scrollWidth > bar.clientWidth + 2;
  band.classList.toggle('overflow', over);
  if (over) {
    ($('wscroll-l') as HTMLButtonElement).disabled = bar.scrollLeft <= 1;
    ($('wscroll-r') as HTMLButtonElement).disabled = bar.scrollLeft >= bar.scrollWidth - bar.clientWidth - 1;
  }
  if (weapon !== bandWeapon) {
    bandWeapon = weapon;
    bar.querySelector<HTMLElement>('button.slot.selected')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}
function scrollBand(dir: number): void {
  const bar = $('weapons');
  bar.scrollBy({ left: dir * Math.max(120, bar.clientWidth * 0.6), behavior: 'smooth' });
  sound.click();
}
$('wscroll-l').onclick = () => scrollBand(-1);
$('wscroll-r').onclick = () => scrollBand(1);
$('weapons').addEventListener('wheel', (e) => {
  const bar = $('weapons');
  if (bar.scrollWidth <= bar.clientWidth) return;
  e.preventDefault();
  bar.scrollLeft += e.deltaY + e.deltaX;
}, { passive: false });

function renderEndTurn(mm: MatchState): void {
  const b = $('endturn');
  const shown = mine() && (mm.phase === PHASE_AIM || mm.phase === PHASE_RETREAT || mm.phase === PHASE_GHOST);
  b.style.display = shown ? 'block' : 'none';
  if (!shown) return;
  const armed = mm.phase === PHASE_AIM && performance.now() < endArmedUntil;
  b.classList.toggle('armed', armed);
  const label = armed ? 'End your turn?' : mm.phase === PHASE_RETREAT ? 'End the retreat' : mm.phase === PHASE_GHOST ? 'Skip'
    : mm.pistolShots > 0 ? 'End the turn' : 'End turn';
  const span = b.querySelector('span')!;
  if (span.textContent !== label) { span.textContent = label; b.setAttribute('aria-label', label); }
}

/** Puts the jetpack on the unit of the turn (one of the team's jetpacks): then the jump key thrusts. */
function putJetpackOn(): void {
  const mm = m();
  const u = selectedUnit();
  if (!mm || !u || !canAct() || u.jet > 0 || ammoLeft(mm, mm.currentPlayer, WEAPON_JETPACK) === 0) return;
  send({ type: 'jetpack', player: mm.currentPlayer, unit: u.id });
}

// --- touch screens (phones, tablets) --------------------------------------------------------------------------
/**
 * One finger on the view aims while the unit of the turn may fire (it puts the cursor; FIRE shoots), and moves the
 * view otherwise (a tap is a click); two fingers move the view, a pinch switches the wide view. On-screen buttons:
 * a round pad walks (the thumb slides left or right), JUMP jumps (held: the jetpack's thrust), FIRE (held to charge),
 * ⟲ ⟳ turn the remote's square, ☰ the pause.
 */
function setTouch(on: boolean): void {
  if (on === touchUi) return;
  touchUi = on;
  document.body.classList.toggle('touch', on);
  if (!on) aimCell = null;
}
setTouch(matchMedia('(pointer: coarse)').matches);
window.addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'mouse') { if (!fromTouch()) setTouch(false); } else { lastTouchAt = performance.now(); setTouch(true); }
}, { capture: true });

/** The finger aims (or steers the flamethrower, the drill) rather than moving the view. */
function fingerAims(): boolean {
  const mm = m();
  if (!mm || !canAct()) return false;
  return holding || !!drilling?.held || (mm.phase === PHASE_AIM && !mm.fired);
}

/** The cursor at the screen point (x, y) in CSS pixels, held on its cell when the view moves. */
function pointAt(x: number, y: number): void {
  mouse = { x, y, inside: true };
  aimCell = [cam.x + (x * dpr()) / cam.scale, cam.y + (y * dpr()) / cam.scale];
}

/** The cursor follows the cell the finger put it on (the view may have moved since). */
function followAimCell(): void {
  if (!touchUi || !aimCell) return;
  mouse = { x: ((aimCell[0] - cam.x) * cam.scale) / dpr(), y: ((aimCell[1] - cam.y) * cam.scale) / dpr(), inside: true };
}

/** After ACTION on a touch screen: the cursor up and ahead of the unit (towards the middle of the map). */
function defaultAim(): void {
  const u = selectedUnit();
  if (!touchUi || aimCell || !u || !canAct()) return;
  const [ux, uy] = unitCenterCells(u);
  aimCell = [ux + (ux < MAP_W / 2 ? 1 : -1) * CROSSHAIR, uy - CROSSHAIR];
}

/** Pinch: past these ratios of the fingers' distance, the view switches (the game has two scales). */
const PINCH_IN = 0.75;
const PINCH_OUT = 1.33;

const gestures = new Gestures({
  down: () => (fingerAims() ? 'aim' : 'pan'),
  aim: (x, y) => pointAt(x, y),
  aimEnd: () => {},
  tap: (x, y) => { pointAt(x, y); primaryPress(); },
  pan: (dx, dy) => { cam.panByScreen(dx * dpr(), dy * dpr()); director.takeOver(); },
  pinch: (ratio, cx, cy) => {
    if (ratio < PINCH_IN && !wide) { setWide(true, cx, cy); gestures.rebasePinch(); }
    else if (ratio > PINCH_OUT && wide) { setWide(false, cx, cy); gestures.rebasePinch(); }
  },
});
canvas.addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'mouse') return;
  e.preventDefault();
  canvas.setPointerCapture(e.pointerId);
  gestures.down(e.pointerId, e.clientX, e.clientY, performance.now());
});
canvas.addEventListener('pointermove', (e) => { if (e.pointerType !== 'mouse') gestures.move(e.pointerId, e.clientX, e.clientY); });
canvas.addEventListener('pointerup', (e) => {
  if (e.pointerType === 'mouse') return;
  lastTouchAt = performance.now();
  gestures.up(e.pointerId, e.clientX, e.clientY, performance.now());
});
canvas.addEventListener('pointercancel', (e) => { if (e.pointerType !== 'mouse') gestures.cancel(e.pointerId); });

/** A button held with a finger (or the mouse): `press` when it touches, `release` once when it is let go. */
function holdButton(el: HTMLElement, press: () => void, release: () => void): void {
  let held = false;
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    held = true;
    el.classList.add('down');
    press();
  });
  const up = () => {
    if (!held) return;
    held = false;
    el.classList.remove('down');
    release();
  };
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);
  el.addEventListener('lostpointercapture', up);
  el.addEventListener('contextmenu', (e) => e.preventDefault());
}
walkPad($('t-pad'));
holdButton($('t-jump'), jumpDown, jumpUp);
holdButton($('t-fire'), () => {
  const mm = m();
  defaultAim();
  followAimCell();
  if (mm && canAct() && !mm.fired && mm.phase === PHASE_AIM) weaponDown();
}, weaponUp);
$('t-rotl').onclick = () => { turnRemote(-1); sound.click(); };
$('t-rotr').onclick = () => { turnRemote(1); sound.click(); };
$('t-pause').onclick = () => openPause(true);
$('t-zoom').onclick = () => { setWide(!wide, window.innerWidth / 2, window.innerHeight / 2); sound.click(); };
$('rotate-hint').onclick = () => { rotateHintClosed = true; };
let rotateHintClosed = false;

/**
 * The walking pad: the thumb touches anywhere in the circle and slides; the knob follows it (held inside the circle)
 * and the unit walks towards the side it leans to (`padState`). Released, the knob goes back to the middle.
 */
function walkPad(pad: HTMLElement): void {
  const knob = pad.querySelector<HTMLElement>('.knob')!;
  let finger = -1;
  let dir = 0;
  const steer = (next: number) => {
    if (next === dir) return;
    dir = next;
    keys.left = dir < 0;
    keys.right = dir > 0;
    sendWalk();
  };
  const follow = (e: PointerEvent) => {
    const r = pad.getBoundingClientRect();
    const s = padState(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2), r.width / 2);
    knob.style.transform = `translate(${s.kx}px, ${s.ky}px)`;
    steer(s.dir);
  };
  pad.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (finger >= 0) return;
    finger = e.pointerId;
    pad.setPointerCapture(e.pointerId);
    pad.classList.add('down');
    follow(e);
  });
  pad.addEventListener('pointermove', (e) => { if (e.pointerId === finger) follow(e); });
  const up = (e: PointerEvent) => {
    if (e.pointerId !== finger) return;
    finger = -1;
    pad.classList.remove('down');
    knob.style.transform = '';
    steer(0);
  };
  pad.addEventListener('pointerup', up);
  pad.addEventListener('pointercancel', up);
  pad.addEventListener('lostpointercapture', up);
  pad.addEventListener('contextmenu', (e) => e.preventDefault());
}

/** What FIRE does with the weapon in hand. */
function fireLabel(): string {
  const mode = WEAPONS[weapon].mode;
  if (mode === 'drill') return 'DRILL';
  if (mode === 'target') return 'DROP';
  if (mode === 'link') return linkTarget < 0 ? 'BIND' : 'THROW';
  if (mode === 'remote') return remotePick ? 'DROP' : 'PICK';
  return 'FIRE';
}

/** The touch controls of the moment (CSS hides them all without a touch screen). */
function renderTouch(mm: MatchState): void {
  const acting = canAct();
  $('movepad').style.display = acting ? 'flex' : 'none';
  // JUMP stays while the unit may move (the retreat too); FIRE only while it may fire.
  $('firepad').style.display = acting ? 'flex' : 'none';
  const firing = acting && ((mm.phase === PHASE_AIM && !mm.fired) || holding || !!drilling?.held);
  $('t-fire').style.display = firing ? 'block' : 'none';
  if (!firing) { $('t-rotl').style.display = 'none'; $('t-rotr').style.display = 'none'; }
  else {
    const label = fireLabel();
    const b = $('t-fire');
    if (b.textContent !== label) b.textContent = label;
    const turning = !!remotePick && WEAPONS[weapon].mode === 'remote';
    $('t-rotl').style.display = turning ? 'block' : 'none';
    $('t-rotr').style.display = turning ? 'block' : 'none';
  }
  $('touchbar').style.display = 'flex';
  $('t-zoom').classList.toggle('on', wide);
  // A phone held upright: the view is narrow, a hint to turn it.
  const upright = touchUi && !rotateHintClosed && window.innerHeight > window.innerWidth && window.innerWidth < 800;
  $('rotate-hint').style.display = upright ? 'block' : 'none';
}

// --- pause menu (Échap) ---------------------------------------------------------------------------------
/** On this PC the match stops while the menu is open; online it goes on (the others keep playing). */
let paused = false;
function openPause(open: boolean): void {
  if (!state || !$('setup').classList.contains('hidden') || !$('end').classList.contains('hidden')) open = false;
  paused = open && !online;
  $('pause').classList.toggle('hidden', !open);
  $('pause-note').textContent = online ? 'Online, the match goes on while paused.' : 'The match is paused.';
  if (open) { keys.left = keys.right = false; charging = null; sendWalk(); }
  renderKickList();
}

/**
 * Online, for the host (author's addition): the other players, each with « exclure » (a second click within 3 s
 * confirms). The server then sends the excluded player's `forfeit`: its invaders vanish, it plays no more turns.
 */
let kickArmed = { seat: -1, until: 0 };
function renderKickList(): void {
  const mm = m();
  const session = online;
  const hosting = !!session && !!mm && session.seat >= 0 && session.room.host === session.seat && mm.phase !== PHASE_OVER;
  $('pause-kick').classList.toggle('hidden', !hosting);
  if (!hosting) return;
  const armed = kickArmed.until > performance.now() ? kickArmed.seat : -1;
  $('kick-list').innerHTML = session.room.players.filter((p) => p.seat !== session.seat).map((p) => {
    const out = p.kicked === true || isOut(mm, p.seat);
    const action = out ? '<small>kicked</small>'
      : `<button class="linkbtn kick${armed === p.seat ? ' armed' : ''}" data-seat="${p.seat}" title="Kick ${escapeHtml(p.name)}: their invaders vanish">${armed === p.seat ? 'confirm?' : 'kick'}</button>`;
    const away = !out && !p.connected ? ' <small>(disconnected)</small>' : '';
    return `<li><span style="color:${TEAM_COLORS[p.seat]};font-weight:600">${escapeHtml(p.name)}${away}</span>${action}</li>`;
  }).join('');
  $('kick-list').querySelectorAll<HTMLButtonElement>('button.kick').forEach((b) => {
    b.onclick = () => {
      const seat = Number(b.dataset.seat);
      if (kickArmed.seat === seat && kickArmed.until > performance.now()) {
        kickArmed = { seat: -1, until: 0 };
        lobby.kick(seat);
      } else {
        kickArmed = { seat, until: performance.now() + KICK_CONFIRM_MS };
        window.setTimeout(renderKickList, KICK_CONFIRM_MS + 50);
      }
      sound.click();
      renderKickList();
    };
  });
}
buildOptionsPanel($('pause-options'), false);
$('pause-resume').onclick = () => openPause(false);
$('pause-quit').onclick = () => { openPause(false); quitMatch(); };

/** Back to the setup screen (online: the seat is left). */
function quitMatch(): void {
  if (online) { online = null; lobby.leave(); }
  state = null;
  for (const id of ['teams', 'timer', 'queue', 'wind', 'banner', 'weaponname']) $(id).textContent = '';
  for (const id of ['weaponband', 'reserve', 'unitui', 'roster', 'endturn', 'cancel', 'movepad', 'firepad', 'touchbar', 'rotate-hint']) $(id).style.display = 'none';
  $('announce').classList.remove('show');
  music.play('menu');
  sound.setWind(0);
  $('end').classList.add('hidden');
  $('setup').classList.remove('hidden');
}

// --- HUD (DOM) ------------------------------------------------------------------------------------
function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

function renderTeams(s: GameState): void {
  const mm = s.match!;
  const html: string[] = [];
  for (let p = 0; p < mm.config.players; p++) {
    const units = playerUnits(s, p);
    const total = units.reduce((t, u) => t + u.hp, 0);
    const segs = [];
    for (let i = 0; i < mm.config.unitsPerPlayer; i++) {
      const u = units[i];
      const pct = u ? (u.hp / UNIT_HP) * 100 : 0;
      segs.push(`<span class="seg"><i style="width:${pct}%;background:${TEAM_COLORS[p]}"></i></span>`);
    }
    const cls = (mm.phase === PHASE_AIM || mm.phase === PHASE_RETREAT) && mm.currentPlayer === p ? 'team current' : 'team';
    const away = isOut(mm, p) ? ' <small>(kicked)</small>'
      : online && online.room.players[p] && !online.room.players[p].connected ? ' <small>(disconnected)</small>' : '';
    const you = online && online.seat === p ? ' <small>(you)</small>' : '';
    html.push(`<div class="${cls}"><div class="row" style="color:${TEAM_COLORS[p]}"><span>${escapeHtml(teamNames[p])}${you}${away}</span><span>${total}</span></div><div class="bar">${segs.join('')}</div></div>`);
  }
  $('teams').innerHTML = html.join('');
}

function escapeHtml(t: string): string {
  return t.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function renderTurn(s: GameState): void {
  const mm = s.match!;
  const left = phaseTicksLeft(mm);
  $('timer').textContent = mm.phase === PHASE_RESOLVE || mm.phase === PHASE_OVER ? '·' : String(Math.ceil(left / TICKS_PER_SECOND));
  // Current turn, then the next 4 (the players who play turns, lobby order: the eliminated ones only with the ghost turns).
  const alive = (p: number) => (mm.phase === PHASE_PLACEMENT ? !isOut(mm, p) : inRotation(s, p));
  const order: number[] = [mm.currentPlayer];
  for (let k = 1; order.length < 5 && k < 40; k++) {
    const p = (mm.currentPlayer + k) % mm.config.players;
    if (alive(p)) order.push(p);
  }
  $('queue').innerHTML = order.map((p, i) => `<span class="${i === 0 ? 'now' : ''}" style="background:${TEAM_COLORS[p]}"></span>`).join('');
  const w = s.wind;
  // GDD: once the lobby rounds are over, the height of the next rise of the lava is added (« lava +5 »).
  const rise = lavaRiseAt(mm, mm.round + 1);
  const wind = s.map.airless ? 'no wind' : `wind ${w > 0 ? '››' : w < 0 ? '‹‹' : '·'} ${Math.abs(w)}`;
  // Île de Braise: the countdown of the eruption (it is back to the full count on the turn it erupted).
  const n = s.map.eruptionIn;
  const eruption = !s.map.crater ? '' : n === ERUPTION_EVERY ? ' · eruption!' : ` · eruption in ${n} turn${n > 1 ? 's' : ''}`;
  $('wind').textContent = mm.phase === PHASE_PLACEMENT ? '' : `round ${mm.round} · ${wind}` + (rise > 0 ? ` · lava +${rise}` : '') + eruption;
}

function renderBars(s: GameState, now: number): void {
  const mm = s.match!;
  const weapons = $('weapons');
  const reserve = $('reserve');
  // Weapon bar: after ACTION, while the unit may still fire.
  if (mm.locked && mm.phase === PHASE_AIM && !mm.fired && mine()) {
    $('weaponband').style.display = 'flex';
    // GDD: the base arsenal (the weapons the host kept), then (after a gap) the chest weapons the team holds.
    const base = BAR_ORDER.filter((i) => (mm.config.ammo?.[i] ?? WEAPONS[i].ammo) !== 0 || ammoLeft(mm, mm.currentPlayer, i) !== 0);
    const found = WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest && ammoLeft(mm, mm.currentPlayer, i) !== 0);
    const key = [...base, '|', ...found].join(',');
    if (weapons.dataset.key !== key) {
      weapons.dataset.key = key;
      bandWeapon = -1;
      // Each slot shows its key (1 to 9, in the bar's order).
      const slot = (i: number, n: number) => `<button class="slot icon" data-w="${i}" title="${WEAPONS[i].name}${n < 9 ? ` (${n + 1})` : ''}"><img src="${weaponIcon(i)}" alt="${WEAPONS[i].short}" /><sup></sup>${n < 9 ? `<span class="key">${n + 1}</span>` : ''}</button>`;
      weapons.innerHTML = base.map((i, n) => slot(i, n)).join('') + (found.length ? '<span class="slot gap"></span>' + found.map((i, n) => slot(i, base.length + n)).join('') : '');
      weapons.querySelectorAll<HTMLButtonElement>('button.slot').forEach((b) => {
        b.onclick = () => {
          const w = Number(b.dataset.w);
          // The jetpack is put on at once (it is a move, not the shot): the weapon in hand does not change.
          if (WEAPONS[w].mode === 'jetpack') putJetpackOn();
          else {
            if (WEAPONS[w].mode === 'drill' && weapon !== w) beforeDrill = weapon;
            weapon = w;
            resetDesignations();
          }
          sound.click();
        };
      });
    }
    weapons.querySelectorAll<HTMLButtonElement>('button.slot').forEach((b) => {
      const i = Number(b.dataset.w);
      const a = ammoLeft(mm, mm.currentPlayer, i);
      b.classList.toggle('selected', i === weapon);
      // After a first bullet, the pistol only, with the bullets left in the turn.
      const firing = mm.pistolShots > 0;
      b.disabled = firing ? i !== WEAPON_PISTOL : a === 0;
      b.querySelector('sup')!.textContent = firing && i === WEAPON_PISTOL ? `${PISTOL_SHOTS - mm.pistolShots}/${PISTOL_SHOTS}` : a < 0 ? '∞' : String(a);
    });
    $('weaponname').textContent = WEAPONS[weapon].name;
    // Weapon names on hover, over the bar.
    weapons.querySelectorAll<HTMLButtonElement>('button.slot').forEach((b) => {
      if (b.matches(':hover')) $('weaponname').textContent = WEAPONS[Number(b.dataset.w)].name;
    });
    renderBand(weapons);
  } else {
    $('weaponband').style.display = 'none';
    $('weaponname').textContent = '';
  }
  // Reserve: during placement, for the player who places.
  if (mm.phase === PHASE_PLACEMENT && mine()) {
    reserve.style.display = 'flex';
    if (!reserve.dataset.built) {
      reserve.dataset.built = '1';
      reserve.innerHTML = INVADERS.map((k, i) => `<button class="slot" data-k="${i}"><canvas width="40" height="40"></canvas>${k.name}</button>`).join('') +
        `<button class="slot rand" id="rand-kind" title="A random invader" aria-label="A random invader">${uiIconHtml('die')}</button>` +
        `<button class="slot rand" id="rand-place" title="Put the chosen invader (or a random one) on a random valid spot" aria-label="Place at random">${uiIconHtml('dieDrop')}</button>`;
      reserve.querySelectorAll<HTMLButtonElement>('.slot[data-k]').forEach((b) => {
        b.onclick = () => { placingKind = Number(b.dataset.k); sound.click(); };
        b.onmouseenter = () => { tipKind = Number(b.dataset.k); };
        b.onmouseleave = () => { tipKind = -1; };
      });
      $('rand-kind').onclick = () => { randomKind(); sound.click(); };
      $('rand-place').onclick = () => { randomPlace(); };
    }
    // A team may have the same invader several times (author's rule): every card stays available.
    reserve.querySelectorAll<HTMLButtonElement>('.slot[data-k]').forEach((b, i) => {
      b.classList.toggle('selected', i === placingKind);
      b.style.borderColor = i === placingKind ? '' : TEAM_COLORS[mm.currentPlayer];
      // The portrait moves when the invader is chosen or under the mouse.
      portrait(b.querySelector('canvas')!, i, i === placingKind || i === tipKind ? Math.floor(now / 200) % 4 : 0);
    });
  } else reserve.style.display = 'none';
}

/** The first weapon of the bar the current team can fire (the host may have left the bazooka out). */
function firstWeapon(mm: MatchState): number {
  const order = [...BAR_ORDER, ...WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest)];
  return order.find((i) => ammoLeft(mm, mm.currentPlayer, i) !== 0 && WEAPONS[i].mode !== 'jetpack' && WEAPONS[i].mode !== 'drill') ?? WEAPON_BAZOOKA;
}

/** Placement: an invader at random, one the player has not placed yet (duplicates are allowed, but chosen). */
function randomKind(): number {
  const mm = m();
  if (!state || !mm || mm.phase !== PHASE_PLACEMENT || !mine()) return -1;
  const free = unusedKinds(state, mm.currentPlayer);
  placingKind = free[Math.floor(Math.random() * free.length)] ?? -1;
  return placingKind;
}

/** Placement: the chosen invader (or one at random) on a valid spot at random of the whole map. */
function randomPlace(): void {
  const mm = m();
  if (!state || !mm || mm.phase !== PHASE_PLACEMENT || !mine()) return;
  const kind = placingKind >= 0 ? placingKind : randomKind();
  if (kind < 0) return;
  const distance = placeDistance(state, mm.currentPlayer);
  for (let attempt = 0; attempt < 400; attempt++) {
    const x = UNIT_W + Math.floor(Math.random() * (MAP_W - 2 * UNIT_W));
    if (!placementSpot(state, mm.currentPlayer, x, 8, distance).ok) continue;
    send({ type: 'place', player: mm.currentPlayer, kind, x, y: 8 });
    sound.place();
    placingKind = -1;
    return;
  }
}

/** Placement: the invader under the mouse in the reserve, animated (its idle loop), with its name and title. */
let tipKind = -1;
function renderTip(now: number): void {
  const tip = $('reserve-tip');
  const kind = touchUi ? placingKind : tipKind;
  if (kind < 0 || $('reserve').style.display === 'none') { tip.style.display = 'none'; return; }
  tip.style.display = 'flex';
  const canvas = tip.querySelector('canvas')!;
  const c = canvas.getContext('2d')!;
  c.clearRect(0, 0, canvas.width, canvas.height);
  drawFrame(c, { x: 0, y: 0, scale: 4 } as Camera, kind, Math.floor(now / 200) % 4, 2, 4, 1);
  tip.querySelector('b')!.textContent = INVADERS[kind].name;
  tip.querySelector('span')!.textContent = INVADERS[kind].title;
}

/** A portrait of an invader (one frame of its sheet, at the canvas's scale); drawn again only when it changes. */
function portrait(canvas: HTMLCanvasElement, kind: number, frame: number): void {
  const key = `${kind} ${frame}`;
  if (canvas.dataset.f === key) return;
  const c = canvas.getContext('2d')!;
  c.clearRect(0, 0, canvas.width, canvas.height);
  if (drawFrame(c, { x: 0, y: 0, scale: canvas.width / FRAME } as Camera, kind, frame, 2, 4, 1)) canvas.dataset.f = key;
}

/** Before ACTION: a card per invader of the team (portrait, name, health, state); the highlighted one moves. */
function renderRoster(s: GameState, now: number): void {
  const mm = s.match!;
  const box = $('roster');
  if (mm.phase !== PHASE_AIM || mm.locked || !mine()) { box.style.display = 'none'; return; }
  box.style.display = 'flex';
  const units = playerUnits(s, mm.currentPlayer).filter((u) => u.alive);
  const cards = box.querySelector<HTMLElement>('.cards')!;
  const key = units.map((u) => u.id).join(',');
  if (cards.dataset.key !== key) {
    cards.dataset.key = key;
    cards.innerHTML = units.map((u) => `<button class="unitcard" data-u="${u.id}"><canvas width="60" height="60"></canvas><b>${INVADERS[u.kind].name}</b><span class="hp"><i></i></span><small></small></button>`).join('');
    cards.querySelectorAll<HTMLButtonElement>('.unitcard').forEach((b) => { b.onclick = () => pickUnit(Number(b.dataset.u)); });
  }
  cards.querySelectorAll<HTMLButtonElement>('.unitcard').forEach((b) => {
    const u = s.units[Number(b.dataset.u)];
    const selected = u.id === mm.selected;
    b.classList.toggle('selected', selected);
    b.disabled = !playable(u);
    b.title = selected ? 'Play this character (click or Enter)' : `Choose ${INVADERS[u.kind].name}`;
    const bar = b.querySelector<HTMLElement>('.hp i')!;
    bar.style.width = `${Math.min(100, (u.hp / UNIT_HP) * 100)}%`;
    bar.style.background = TEAM_COLORS[u.player];
    const note = b.querySelector('small')!;
    const [text, cls] = u.frozen ? ['frozen · skips its turn', 'cold'] : u.burning ? [`${u.hp} HP · on fire`, 'hot'] : [`${u.hp} HP`, ''];
    if (note.textContent !== text) { note.textContent = text; note.className = cls; }
    portrait(b.querySelector('canvas')!, u.kind, selected ? Math.floor(now / 200) % 4 : 0);
  });
}

/** « À toi, … ! » in the middle of the screen at the start of a turn (« Au tour de … » online for the others). */
function announceTurn(s: GameState): void {
  const p = s.match!.currentPlayer;
  const el = $('announce');
  const name = escapeHtml(teamNames[p]);
  const ghost = s.match!.phase === PHASE_GHOST;
  el.innerHTML = !mine() ? `${name}'s turn${ghost ? ' (ghost)' : ''}`
    : ghost ? `Your turn, ${name}! (ghost)<small>drop a chest or lay a mine</small>`
    : `Your turn, ${name}!<small>pick your character, then ${uiIconHtml('fist')}</small>`;
  el.style.color = TEAM_COLORS[p];
  el.classList.remove('show');
  void el.offsetWidth; // restart the animation
  el.classList.add('show');
}

/** A refused click: a low beep, and why in the banner for 2 s. */
let refusal: { text: string; until: number } | null = null;
function refuse(text: string): void {
  refusal = { text, until: performance.now() + 2000 };
  sound.tick(false);
}

function renderBanner(s: GameState): void {
  const mm = s.match!;
  const banner = $('banner');
  if (refusal && performance.now() > refusal.until) refusal = null;
  const alert = online?.lost ? 'Connection lost — reconnecting…' : online?.desync ?? refusal?.text ?? null;
  banner.classList.toggle('alert', alert !== null);
  if (alert) { setBanner(banner, alert); return; }
  if (online && online.lock.upTo - s.tick > 60) { setBanner(banner, `Catching up with the match… ${Math.round((100 * s.tick) / online.lock.upTo)}%`); return; }
  let text = '';
  if (!mine() && (mm.phase === PHASE_PLACEMENT || mm.phase === PHASE_AIM || mm.phase === PHASE_RETREAT)) {
    text = (online && online.seat < 0 ? '👁 Spectator · ' : '') + `${teamNames[mm.currentPlayer]}'s turn` + (canPing() ? ` · ${touchUi ? 'tap' : 'click'}: ping` : '');
  } else if (mm.phase === PHASE_PLACEMENT) {
    const n = playerUnits(s, mm.currentPlayer).length + 1;
    text = `Placement · ${teamNames[mm.currentPlayer]} places their ${ordinal(n)} invader` + (placingKind < 0 ? ' — pick it from the reserve' : ` — ${INVADERS[placingKind].name}: ${touchUi ? 'tap' : 'click'} to place`);
  } else if (mm.phase === PHASE_GHOST) {
    const what = ghostAction === 'chest' ? 'drop a chest here' : 'lay a mine here';
    text = touchUi ? `Ghost turn · tap: ${what}` : `Ghost turn · click: ${what} · C: chest · M: mine · F: skip`;
  } else if (mm.phase === PHASE_AIM && !mm.locked) text = `${teamNames[mm.currentPlayer]} · pick your character, then {fist}`;
  else if (mm.phase === PHASE_AIM && mm.pistolShots > 0 && mine()) {
    const left = PISTOL_SHOTS - mm.pistolShots;
    text = `Pistol · ${left} more bullet${left > 1 ? 's' : ''} this turn` + (touchUi ? '' : ' (click) · F: end the turn');
  } else if (mm.phase === PHASE_AIM && !mm.fired && mine()) {
    text = touchUi ? 'Drag on the map to aim, then FIRE (hold to charge)' : 'Move, pick a weapon (1-9) and fire · right click: cancel';
  }
  else if (mm.phase === PHASE_RETREAT) text = 'Retreat!';
  const flier = selectedUnit();
  if (flier && flier.jet > 0 && mine()) {
    const how = touchUi ? 'hold JUMP to climb, the pad steers' : 'hold Space (or W) to climb, ←/→ (or A/D) to steer';
    text = `Jetpack · ${how} · ${Math.round((100 * flier.jet) / JETPACK_FUEL)} %`;
  } else if (flier && flier.drill > 0 && mine()) {
    text = `Drill · steer it with ${touchUi ? 'a finger on the map, release DRILL' : 'the mouse, release'} to stop · ${Math.round((100 * flier.drill) / DRILL_TICKS)} %`;
  } else if (weapon === WEAPON_DRILL && mm.phase === PHASE_AIM && mm.locked && !mm.fired && mine()) {
    text = touchUi ? 'Drill · hold DRILL to dig your way (3 s), a finger on the map steers it · it is not your shot'
      : 'Drill · hold the click to dig your way (3 s), the mouse steers it · it is not your shot';
  }
  setBanner(banner, text);
  banner.style.color = TEAM_COLORS[mm.currentPlayer];
}

/** The banner's text, `{fist}` drawn as the icon of ACTION; written again only when it changes. */
function setBanner(el: HTMLElement, text: string): void {
  const plain = text.split('{fist}').join('');
  if (el.dataset.text === text && el.textContent === plain) return;
  el.dataset.text = text;
  el.replaceChildren(...text.split('{fist}').flatMap((part, i) => {
    if (i === 0) return [part];
    const img = document.createElement('img');
    img.className = 'pxicon';
    img.src = uiIcon('fist');
    img.alt = 'ACTION';
    return [img, part];
  }));
}

/** ACTION above the head of the highlighted unit, ◀ ▶ around it. */
function placeUnitUi(s: GameState): void {
  const mm = s.match!;
  const ui = $('unitui');
  const u = selectedUnit();
  if (!u || mm.phase !== PHASE_AIM || mm.locked || !playable(u) || !mine()) { ui.style.display = 'none'; return; }
  const [sx, sy] = toScreen(cam, u.x / FP + UNIT_W / 2, u.y / FP);
  ui.style.display = 'flex';
  ui.style.left = `${sx / dpr()}px`;
  ui.style.top = `${(sy - 4 * cam.scale) / dpr() - 2}px`; // over the health bar
  const several = playerUnits(s, mm.currentPlayer).filter((x) => x.alive).length > 1;
  $('prev').style.visibility = several ? 'visible' : 'hidden';
  $('next').style.visibility = several ? 'visible' : 'hidden';
}

// --- ghost turns and pings (author's additions) ---------------------------------------------------
/** Ghost turn: what a click does, a chest over the column or a mine on the ground. */
let ghostAction: 'chest' | 'mine' = 'chest';

function renderGhostUi(mm: MatchState): void {
  const box = $('ghostui');
  const shown = mm.phase === PHASE_GHOST && mine();
  box.style.display = shown ? 'flex' : 'none';
  if (!shown) return;
  $('ghost-chest').classList.toggle('selected', ghostAction === 'chest');
  $('ghost-mine').classList.toggle('selected', ghostAction === 'mine');
  ($('ghost-chest') as HTMLButtonElement).disabled = state!.chests.length >= MAX_CHESTS;
}
$('ghost-chest').onclick = () => { ghostAction = 'chest'; sound.click(); };
$('ghost-mine').onclick = () => { ghostAction = 'mine'; sound.click(); };
$('ghost-chest').innerHTML = `<img src="${uiIcon('chest')}" alt="chest" />`;
$('ghost-mine').innerHTML = `<img src="${weaponIcon(WEAPON_MINE)}" alt="mine" />`;

/** Ghost turn: where the click would drop the chest (from the top of the map) or put the mine (on the ground). */
function drawGhost(s: GameState): void {
  const mm = s.match!;
  if (mm.phase !== PHASE_GHOST || !mine() || !mouse.inside) return;
  const [x, y] = mouseCell();
  const { w, h, data } = s.world.physique;
  if (x < 0 || x >= w) return;
  const open = (yy: number) => s.world.rules.materials.state[data[yy * w + x]] >= STATE_LIQUID;
  let gy = Math.max(0, y);
  while (gy < h && open(gy)) gy++;
  const sc = cam.scale;
  ctx.strokeStyle = 'rgba(200, 220, 255, 0.85)';
  ctx.fillStyle = 'rgba(200, 220, 255, 0.25)';
  ctx.lineWidth = Math.max(1, sc / 2);
  if (ghostAction === 'chest') {
    // The column the chest comes down, and the chest at the top of the view.
    const [sx, top] = toScreen(cam, x, Math.max(0, cam.y + 6));
    const [, ground] = toScreen(cam, x, gy);
    ctx.setLineDash([sc, sc * 3]);
    ctx.beginPath(); ctx.moveTo(sx, top); ctx.lineTo(sx, ground); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillRect(sx - (CHEST_W / 2) * sc, top, CHEST_W * sc, 10 * sc);
    ctx.strokeRect(sx - (CHEST_W / 2) * sc, top, CHEST_W * sc, 10 * sc);
  } else {
    const [sx, sy] = toScreen(cam, x - MINE_HALF, gy - 2 * MINE_HALF - 1);
    ctx.fillRect(sx, sy, (2 * MINE_HALF + 1) * sc, (2 * MINE_HALF + 1) * sc);
    ctx.strokeRect(sx, sy, (2 * MINE_HALF + 1) * sc, (2 * MINE_HALF + 1) * sc);
  }
}

/** Pings (online): a point shown to the whole room, PING_MS long. */
const PING_MS = 3000;
const pings: { x: number; y: number; seat: number; at: number }[] = [];
let lastPing = 0;

/** Who may ping is the host's choice (author's option): nobody, the players (by default), or the spectators too. The server checks too. */
function canPing(): boolean {
  const rule = online?.room.settings.pings ?? 'players';
  return online !== null && (rule === 'all' || (rule === 'players' && online.seat >= 0));
}

function sendPing(): void {
  const now = performance.now();
  if (!online || !canPing() || now - lastPing < 300) return;
  lastPing = now;
  const [x, y] = mouseCell();
  online.net.send({ t: 'ping', x: Math.max(0, Math.min(MAP_W - 1, x)), y: Math.max(0, Math.min(MAP_H - 1, y)) });
}

/** Rings that spread from the point, in the colour of who pinged; off the view, an arrow at its edge. */
function drawPings(now: number): void {
  for (let i = pings.length - 1; i >= 0; i--) if (now - pings[i].at > PING_MS) pings.splice(i, 1);
  const margin = 18 * dpr();
  for (const p of pings) {
    const age = (now - p.at) / PING_MS;
    const color = p.seat >= 0 ? TEAM_COLORS[p.seat % TEAM_COLORS.length] : '#e8eaee';
    const who = p.seat >= 0 ? teamNames[p.seat] ?? '' : 'Spectator';
    const [sx, sy] = toScreen(cam, p.x + 0.5, p.y + 0.5);
    const inside = sx >= 0 && sy >= 0 && sx <= canvas.width && sy <= canvas.height;
    ctx.globalAlpha = 1 - age * 0.8;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    if (inside) {
      ctx.lineWidth = Math.max(2, cam.scale * 0.7);
      for (let k = 0; k < 2; k++) {
        const r = ((age * 2 + k * 0.5) % 1) * 18 * cam.scale + 3 * cam.scale;
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(sx, sy, 2 * cam.scale, 0, Math.PI * 2); ctx.fill();
      ctx.font = `600 ${Math.round(12 * dpr())}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(who, sx, sy - 22 * cam.scale);
    } else {
      // An arrow at the edge of the view, pointing to it.
      const ex = Math.min(canvas.width - margin, Math.max(margin, sx));
      const ey = Math.min(canvas.height - margin, Math.max(margin, sy));
      const a = Math.atan2(sy - ey, sx - ex);
      ctx.save();
      ctx.translate(ex, ey);
      ctx.rotate(a);
      ctx.beginPath(); ctx.moveTo(12 * dpr(), 0); ctx.lineTo(-6 * dpr(), -8 * dpr()); ctx.lineTo(-6 * dpr(), 8 * dpr()); ctx.closePath(); ctx.fill();
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}

// --- canvas overlays ------------------------------------------------------------------------------
/**
 * Hidden placement (the host's option): during the placement, the others' invaders are not shown — to the player who
 * places on this PC, to its own seat online; a spectator sees them all.
 */
function shown(s: GameState, u: Unit): boolean {
  const mm = s.match;
  if (!mm || mm.phase !== PHASE_PLACEMENT || mm.config.hiddenPlacement !== true) return true;
  const viewer = online ? online.seat : mm.currentPlayer;
  return viewer < 0 || u.player === viewer;
}

/**
 * Placement: how far from the enemies the game asks now (the GDD's 48, less when no spot keeps it: up to 8 teams),
 * worked out again when a unit is placed and every second (it scans the map).
 */
let placeCache = { key: '', at: 0, distance: PLACEMENT_ENEMY_DISTANCE };
function placeDistance(s: GameState, player: number): number {
  const key = `${s.units.length} ${player}`;
  const now = performance.now();
  if (key !== placeCache.key || now - placeCache.at > 1000) placeCache = { key, at: now, distance: placementDistance(s, player) };
  return placeCache.distance;
}

function drawPlacement(s: GameState): void {
  const mm = s.match!;
  const distance = placeDistance(s, mm.currentPlayer);
  // GDD: the valid zones in green, the others in red (for the player who places).
  if (mine()) {
    zones.update(s, mm.currentPlayer, cam, performance.now(), distance);
    zones.draw(ctx, cam);
  }
  ctx.lineWidth = Math.max(1, cam.scale / 2);
  // Forbidden area around every enemy (none with the hidden placement: the enemies keep nobody away).
  for (const u of s.units) {
    if (!u.alive || u.player === mm.currentPlayer || mm.config.hiddenPlacement) continue;
    const [sx, sy] = toScreen(cam, ...unitCenterCells(u));
    ctx.strokeStyle = 'rgba(255, 70, 70, 0.8)';
    ctx.fillStyle = 'rgba(255, 70, 70, 0.08)';
    ctx.beginPath();
    ctx.arc(sx, sy, distance * cam.scale, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  if (placingKind < 0 || !mouse.inside || !mine()) return;
  const [x, y] = mouseCell();
  const spot = placementSpot(s, mm.currentPlayer, x, y, distance);
  const gx = spot.x ?? x - UNIT_W / 2;
  const gy = spot.y ?? y - UNIT_H / 2;
  const ghost = createUnit(-1, mm.currentPlayer, placingKind, gx, gy);
  drawUnit(ctx, cam, ghost, animator, performance.now(), 0.6, false);
  const [sx, sy] = toScreen(cam, gx, gy);
  ctx.strokeStyle = spot.ok ? '#5fd35f' : '#ff4646';
  ctx.lineWidth = Math.max(2, cam.scale);
  ctx.strokeRect(sx, sy, UNIT_W * cam.scale, UNIT_H * cam.scale);
  ctx.fillStyle = spot.ok ? '#5fd35f' : '#ff4646';
  ctx.font = '600 13px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(spot.ok ? (touchUi ? 'tap: place' : 'click: place') : spot.reason, sx + (UNIT_W * cam.scale) / 2, sy - 6);
}

function drawMarkers(s: GameState): void {
  const mm = s.match!;
  if (mm.phase !== PHASE_AIM && mm.phase !== PHASE_RETREAT) return;
  ctx.textAlign = 'center';
  ctx.font = `${Math.max(10, 3 * cam.scale)}px system-ui, sans-serif`;
  for (const u of playerUnits(s, mm.currentPlayer)) {
    if (!u.alive) continue;
    const [sx, sy] = toScreen(cam, u.x / FP + UNIT_W / 2, u.y / FP);
    // Over the health bar (3 cells over the head).
    const above = sy - 4 * cam.scale;
    if (u.id === mm.selected) {
      // GDD mockups: the unit of the turn is underlined in yellow; once locked, its name over its head.
      ctx.fillStyle = '#ffd23f';
      ctx.fillRect(sx - 6 * cam.scale, sy + UNIT_H * cam.scale, 12 * cam.scale, cam.scale);
      if (mm.locked) {
        ctx.fillStyle = TEAM_COLORS[u.player];
        ctx.fillText(INVADERS[u.kind].name, sx, above);
      }
    } else if (!mm.locked) {
      ctx.fillStyle = TEAM_COLORS[u.player];
      ctx.fillText('▼', sx, above);
    }
  }
}

function drawAim(s: GameState, now: number): void {
  const mm = s.match!;
  const u = selectedUnit();
  if (!u || !u.alive || !mm.locked || mm.phase !== PHASE_AIM || mm.fired || !mine()) return;
  const mode = WEAPONS[weapon].mode;
  if (mode === 'remote') { drawRemote(s); return; }
  if (mode === 'link' && linkTarget < 0) {
    // The enemies the doll may bind, outlined in violet (pulsing).
    const pulse = 0.55 + 0.45 * Math.sin(now / 160);
    ctx.strokeStyle = `rgba(198, 156, 255, ${pulse})`;
    ctx.lineWidth = Math.max(2, cam.scale);
    for (const t of s.units) {
      if (!t.alive || t.player === mm.currentPlayer) continue;
      const [tx, ty] = toScreen(cam, t.x / FP - 2, t.y / FP - 2);
      ctx.strokeRect(tx, ty, (UNIT_W + 4) * cam.scale, (UNIT_H + 4) * cam.scale);
    }
    ctx.fillStyle = '#c69cff';
    ctx.font = '600 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(touchUi ? 'put the cursor on an enemy, then BIND' : 'pick an enemy to bind', mouse.x * dpr(), mouse.y * dpr() - 14);
    return;
  }
  if (mode === 'link') {
    const t = s.units[linkTarget];
    if (t?.alive) {
      const [tx, ty] = toScreen(cam, ...unitCenterCells(t));
      ctx.strokeStyle = 'rgba(198, 156, 255, 0.9)';
      ctx.lineWidth = Math.max(2, cam.scale);
      ctx.strokeRect(tx - 9 * cam.scale, ty - 9 * cam.scale, 18 * cam.scale, 18 * cam.scale);
    }
  }
  if (mode === 'target') {
    // Designate a point: a marker under the cursor and the column the block will fall down.
    const [tx, ty] = mouseCell();
    const [sx, sy] = toScreen(cam, tx - 7, ty - 7);
    ctx.strokeStyle = '#a8e4f4';
    ctx.lineWidth = Math.max(1, cam.scale / 2);
    ctx.strokeRect(sx, sy, 14 * cam.scale, 14 * cam.scale);
    ctx.setLineDash([cam.scale, cam.scale * 3]);
    ctx.beginPath(); ctx.moveTo(sx + 7 * cam.scale, 0); ctx.lineTo(sx + 7 * cam.scale, sy); ctx.stroke();
    ctx.setLineDash([]);
    return;
  }
  const a = (aimAngle(u) * Math.PI) / 180;
  const [ux, uy] = unitCenterCells(u);
  const [sx, sy] = toScreen(cam, ux + Math.cos(a) * CROSSHAIR, uy - Math.sin(a) * CROSSHAIR);
  const r = Math.max(4, 2.5 * cam.scale);
  ctx.strokeStyle = '#ffd23f';
  ctx.lineWidth = Math.max(1, cam.scale / 2);
  ctx.beginPath();
  ctx.arc(sx, sy, r, 0, Math.PI * 2);
  ctx.moveTo(sx - r * 1.6, sy); ctx.lineTo(sx + r * 1.6, sy);
  ctx.moveTo(sx, sy - r * 1.6); ctx.lineTo(sx, sy + r * 1.6);
  ctx.stroke();
  if (charging && WEAPONS[weapon].lob) {
    // A throw (author's rule): the dotted aim follows the start of the arc for the power charged, without the wind.
    const power = chargePower(now);
    drawThrowArc(ctx, cam, throwArc(s, u, weapon, aimAngle(u), power), power);
    return;
  }
  // Dotted aim line.
  const [x0, y0] = toScreen(cam, ux, uy);
  ctx.setLineDash([cam.scale, cam.scale * 2]);
  ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(sx, sy); ctx.stroke();
  ctx.setLineDash([]);
  if (charging) {
    // Power gauge: a wedge that grows along the aim, from yellow to red.
    const p = chargePower(now) / 100;
    const [ex, ey] = toScreen(cam, ux + Math.cos(a) * CROSSHAIR * p, uy - Math.sin(a) * CROSSHAIR * p);
    ctx.strokeStyle = `rgb(255, ${Math.round(210 - 170 * p)}, 40)`;
    ctx.lineWidth = Math.max(3, cam.scale * 2);
    ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(ex, ey); ctx.stroke();
  }
}

/** Remote: the square under the cursor, then the turned square at its destination (green: valid). */
function drawRemote(s: GameState): void {
  const [x, y] = mouseCell();
  const half = REMOTE_SIZE >> 1;
  const sc = cam.scale;
  const square = (cx: number, cy: number, rot: number, color: string) => {
    const [sx, sy] = toScreen(cam, cx, cy);
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate((rot * REMOTE_STEP_DEG * Math.PI) / 180);
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, sc / 2);
    ctx.strokeRect(-half * sc, -half * sc, REMOTE_SIZE * sc, REMOTE_SIZE * sc);
    ctx.restore();
  };
  if (!remotePick) { square(x, y, 0, '#ffd23f'); return; }
  square(remotePick.sx, remotePick.sy, 0, 'rgba(255, 210, 63, 0.6)');
  const why = remoteRefusal(s, { sx: remotePick.sx, sy: remotePick.sy, tx: x, ty: y, rot: remoteRot });
  square(x, y, remoteRot, why ? '#ff4646' : '#5fd35f');
  // The reason (or how to drop it) over the square, kept on screen, outlined to be read on any ground.
  const text = why ?? (touchUi ? `DROP · ⟲ ⟳: ${remoteRot * REMOTE_STEP_DEG}°` : `click: drop · wheel: ${remoteRot * REMOTE_STEP_DEG}°`);
  ctx.font = `600 ${Math.round(14 * dpr())}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  const [lx, ly] = toScreen(cam, x, y - half - 4);
  const ty = Math.min(canvas.height - 8 * dpr(), Math.max(22 * dpr(), ly));
  ctx.lineWidth = 4 * dpr();
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.strokeText(text, lx, ty);
  ctx.fillStyle = why ? '#ff6b6b' : '#5fd35f';
  ctx.fillText(text, lx, ty);
}

// --- loop -----------------------------------------------------------------------------------------
function update(): void {
  if (!state) return;
  if (drilling) {
    // The drill follows the aim while held; once it stops, the weapon in hand before it comes back.
    const mm = m();
    const u = selectedUnit();
    const active = !!u && u.drill > 0;
    if (!mm || !u || (!active && performance.now() - drilling.since > 500)) {
      drilling = null;
      if (weapon === WEAPON_DRILL) weapon = beforeDrill;
    } else if (active && drilling.held) {
      const a = aimAngle(u);
      if (a !== lastAim) { lastAim = a; send({ type: 'aim', player: mm.currentPlayer, unit: u.id, angle: a }); }
    }
  }
  if (holding) {
    const mm = m();
    const u = selectedUnit();
    if (!mm || !u || !state.jets.some((j) => j.unit === u.id)) holding = false;
    else {
      const a = aimAngle(u);
      if (a !== lastAim) { lastAim = a; send({ type: 'aim', player: mm.currentPlayer, unit: u.id, angle: a }); }
    }
  }
  if (online) {
    // Only the ticks the server confirmed; more than one at a time to catch up (then without effects).
    const n = online.lock.ticksToRun(state.tick);
    for (let i = 0; i < n && state; i++) simulate(online.lock.take(state.tick), n > 30);
    return;
  }
  const batch = pending;
  pending = [];
  for (const c of batch) c.tick = state.tick; // commands sent between ticks apply to the next one
  log.push(...batch);
  simulate(batch, false);
}

/** One tick of the match, then what the page does with it (effects, camera, turn changes, end). */
function simulate(batch: readonly Command[], quiet: boolean): void {
  if (!state) return;
  tick(state, batch);
  if (online && online.seat >= 0 && state.tick % HASH_EVERY_TICKS === 0) {
    // GDD section 13: a fingerprint every 5 s, compared by the server between the browsers.
    const hash = hashState(state);
    online.hashes[state.tick] = hashToHex(hash);
    online.net.send({ t: 'hash', tick: state.tick, hash });
  }
  const mm = state.match!;
  for (const e of state.events) {
    if (e.type !== 'damage' && e.type !== 'death') continue;
    const t = (tally[e.unit] ??= { damage: 0, died: 0, offMap: false, hp: UNIT_HP });
    if (e.type === 'damage') { t.damage += Math.min(e.amount, t.hp); t.hp = Math.max(0, t.hp - e.amount); }
    else { t.died = mm.round; t.offMap = e.offMap; }
  }
  for (const u of state.units) (tally[u.id] ??= { damage: 0, died: 0, offMap: false, hp: UNIT_HP }).hp = Math.max(0, u.hp);
  if (online && online.seat >= 0 && !online.resultSent && mm.phase === PHASE_OVER) {
    // The admin page's record of the match: computed from the match alone, so the same in every browser.
    online.resultSent = true;
    online.net.send({ t: 'result', tick: state.tick, result: matchResult(state) });
  }
  if (!quiet) {
    effects.onEvents(state.events, state.world, (id) => {
      const u = state!.units[id];
      return u ? [u.x / FP + UNIT_W / 2, u.y / FP - 2] : null;
    });
    director.onEvents(state.events);
    animator.onEvents(state, state.events, performance.now());
    sound.onEvents(state.events, (id) => { const u = state!.units[id]; return u ? [u.x / FP + UNIT_W / 2, u.y / FP + UNIT_H / 2] : null; });
  }
  // The last 5 seconds of a timer tick (placement, aim, retreat).
  const seconds = Math.ceil(phaseTicksLeft(mm) / TICKS_PER_SECOND);
  if (seconds !== lastSeconds && seconds > 0 && seconds <= 5 && !quiet) sound.tick(seconds === 1);
  lastSeconds = seconds;
  if (mm.turn !== lastTurn && (mm.phase === PHASE_AIM || mm.phase === PHASE_GHOST)) {
    lastTurn = mm.turn;
    endArmedUntil = 0;
    if (!quiet) { sound.turn(); announceTurn(state); }
    keys.left = keys.right = false;
    charging = null;
    resetDesignations();
    if (touchUi) { aimCell = null; mouse.inside = false; }
    if (ammoLeft(mm, mm.currentPlayer, weapon) === 0) weapon = firstWeapon(mm);
    $('weapons').dataset.key = '';
    if (lastTurn === 1) { wide = false; fit(); }
    const u = selectedUnit();
    if (u) director.glideTo(cam, ...unitCenterCells(u));
  }
  if (mm.phase === PHASE_OVER && $('end').classList.contains('hidden') && (!online || online.lock.upTo - state.tick < 60)) {
    $('endtitle').textContent = mm.winner === DRAW ? 'Draw!' : `${teamNames[mm.winner]} wins!`;
    $('endtitle').style.color = mm.winner === DRAW ? '#fff' : TEAM_COLORS[mm.winner];
    $('end').classList.remove('hidden');
    music.play('none');
    sound.victory(mm.winner === DRAW);
    if (!online || online.seat >= 0) track('finish', { mode: online ? 'online' : 'local' });
  }
}

function matchResult(s: GameState): MatchResult {
  const mm = s.match!;
  return {
    winner: mm.winner, round: mm.round, turn: mm.turn, lava: mm.lavaLevel,
    units: s.units.map((u) => {
      const t = tally[u.id];
      return { player: u.player, kind: u.kind, hp: Math.max(0, u.hp), damage: t?.damage ?? 0, died: t?.died ?? 0, offMap: t?.offMap ?? false };
    }),
  };
}

let lastFrame = performance.now();
let lastLocked = false;
function render(): void {
  const now = performance.now();
  const dt = Math.min(0.05, (now - lastFrame) / 1000);
  lastFrame = now;
  if (state) {
    if (mouse.inside && !rightPress && !touchUi) {
      const d = EDGE_SCROLL_CELLS_PER_S * dt * cam.scale;
      let moved = false;
      if (mouse.x < EDGE_SCROLL_PX) { cam.panByScreen(d, 0); moved = true; }
      else if (mouse.x > window.innerWidth - EDGE_SCROLL_PX) { cam.panByScreen(-d, 0); moved = true; }
      if (mouse.y < EDGE_SCROLL_PX) { cam.panByScreen(0, d); moved = true; }
      else if (mouse.y > window.innerHeight - EDGE_SCROLL_PX) { cam.panByScreen(0, -d); moved = true; }
      if (moved) director.takeOver();
    }
    director.update(dt, state, cam);
    effects.update(dt);
    // Touch: after ACTION, the cursor starts again ahead of the unit; it stays on its cell when the view moves.
    const locked = state.match!.locked;
    if (locked !== lastLocked) { lastLocked = locked; if (locked && touchUi) aimCell = null; }
    defaultAim();
    followAimCell();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#0d0e10';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (!state) { minimap.canvas.style.display = 'none'; return; }
  // The wind ambience follows the match's wind (none on the Moon).
  sound.setWind(state.map.airless ? 0 : Math.abs(state.wind) / 10);
  minimap.canvas.style.display = '';
  const [shx, shy] = effects.shakeOffset(cam);
  ctx.setTransform(1, 0, 0, 1, shx, shy);
  applyLook(state.map.id);
  backdrop?.draw(ctx, cam);
  renderer.draw(ctx, state.world, cam);
  effects.drawBack(ctx, cam);
  drawSteles(ctx, cam, state);
  drawChests(ctx, cam, state);
  drawMines(ctx, cam, state, now);
  drawBatteries(ctx, cam, state, now);
  drawMachines(ctx, cam, state, now);
  for (const u of state.units) if (shown(state, u)) drawUnit(ctx, cam, u, animator, now);
  drawImmersion(ctx, cam, state);
  animator.drawDeaths(ctx, cam, now);
  drawProjectiles(ctx, cam, state);
  drawSurges(ctx, cam, state, now);
  effects.jets(state);
  effects.ambient(state, cam);
  effects.drawFront(ctx, cam);
  const mm = state.match!;
  if (mm.phase === PHASE_PLACEMENT) drawPlacement(state);
  drawMarkers(state);
  drawAim(state, now);
  drawGhost(state);
  drawPings(now);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  minimap.draw(state.world, cam, now, mm.phase === PHASE_PLACEMENT ? state.units.filter((u) => shown(state!, u)) : state.units);
  renderTeams(state);
  renderTurn(state);
  renderBars(state, now);
  renderBanner(state);
  placeUnitUi(state);
  renderRoster(state, now);
  renderEndTurn(mm);
  renderGhostUi(mm);
  $('cancel').style.display = cancellable() ? 'block' : 'none';
  renderTouch(mm);
  renderTip(now);
}

window.__match = {
  state: () => state, send, camera: cam, names: () => teamNames,
  seat: () => online?.seat ?? -1, hashes: () => online?.hashes ?? {}, desync: () => online?.desync ?? null,
};
startFixedLoop(update, render, () => !paused);
bootDone(spritesReady);
// The players' path (admin page): the visit if it began here (a shared link, Discord), then the match page.
track('visit', { page: 'match' });
track('play');
